/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  FormArray,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  Validators,
} from '@angular/forms';

import {
  CreateFormField,
  CreateSection,
  CustomFormField,
  CustomSectionForm,
  CustomTemplateForm,
  FieldForm,
  IFormField,
  IFormSection,
  IFormTemplate,
  SectionForm,
  TemplateForm,
} from './interfaces';
import { FieldType } from './enums';

// Interface for translated strings
export interface TranslatedStrings {
  templateTitle: string;
  templateDescription: string;
  questionTitle: string;
  fileFieldHelpText: string;
  answerText: string;
  locationTitle: string;
}

export function filterFormTemplate(
  templates: IFormTemplate[],
  templateId: string,
): IFormTemplate {
  const template = templates.find((template) => template.id === templateId)!;
  return template;
}

export function filterFormTemplates(
  templates: IFormTemplate[],
  query: string,
): IFormTemplate[] {
  if (!query) return templates;
  return templates.filter(
    (template) =>
      template.name.toLowerCase().includes(query.toLowerCase()) ||
      template.updatedBy?.toLowerCase().includes(query.toLowerCase()),
  );
}

export function createNewTemplateForm(): CustomTemplateForm {
  const newForm = new FormGroup<TemplateForm>({
    id: new FormControl('template', { nonNullable: true }),
    name: new FormControl('', { nonNullable: true }),
    description: new FormControl('', { nonNullable: true }),
    sections: new FormArray<CustomSectionForm>([
      new FormGroup<SectionForm>({
        id: new FormControl('section', { nonNullable: true }),
        name: new FormControl('Section', { nonNullable: true }),
        description: new FormControl('', { nonNullable: true }),
        order: new FormControl(1, { nonNullable: true }),
        fields: new FormArray<CustomFormField>([]),
      }),
    ]),
    assignedTo: new FormArray<FormControl<string>>([]),
    updatedBy: new FormControl('', { nonNullable: true }),
    updatedAt: new FormControl(new Date(), { nonNullable: true }),
  });
  return newForm;
}

export function createTemplateFormFromExisting(
  template: IFormTemplate,
  fb: NonNullableFormBuilder,
): CustomTemplateForm {
  return fb.group<TemplateForm>({
    id: fb.control(template.id),
    name: fb.control(template.name),
    description: fb.control(template.description ?? ''),
    sections: fb.array<CustomSectionForm>(
      template.sections.map((section: IFormSection) => {
        return fb.group<SectionForm>({
          id: fb.control(section.id ?? crypto.randomUUID()),
          name: fb.control(section.name),
          description: fb.control(section.description ?? ''),
          order: fb.control(section.order),
          fields: fb.array<CustomFormField>(
            section.fields.map((field) => createFieldFormFromData(fb, field)),
          ),
        });
      }),
    ),
    assignedTo: fb.array<FormControl<string>>([]),
    updatedBy: fb.control(template.updatedBy ?? ''),
    updatedAt: fb.control(template.updatedAt ? template.updatedAt : new Date()),
  });
}

export function createFieldFormFromData(
  fb: NonNullableFormBuilder,
  data: IFormField,
): FormGroup<FieldForm> {
  const valueControl = fb.control((data.value as string) ?? '');
  if (data.type === FieldType.LOCATION) {
    valueControl.disable();
  }

  const placeholderControl = fb.control(data.placeholder ?? 'Answer');
  // Always disable placeholder control to make it non-editable
  placeholderControl.disable();

  return fb.group<FieldForm>({
    id: fb.control(data._id ?? crypto.randomUUID()),
    question: fb.control(data.question),
    description: fb.control(data.description ?? ''),
    type: fb.control(data.type, { validators: [Validators.required] }),
    label: fb.control(data.label ?? ''),
    placeholder: placeholderControl,
    helpText: fb.control(data.helpText ?? ''),
    required: fb.control(data.required, { validators: [Validators.required] }),
    order: fb.control(data.order, { validators: [Validators.required] }),
    options: fb.array<FormControl<string>>(
      (data.options ?? []).map((option) => fb.control(option)),
    ),
    value: valueControl,
  });
}

export function createNewFieldForm(
  type: FieldType,
  order: number,
  translatedFileHelpText: string = '',
): IFormField {
  const field: IFormField = {
    _id: crypto.randomUUID(),
    question: translatedFileHelpText || 'Untitled Question',
    description: '',
    type,
    label: '',
    placeholder: '',
    helpText: type === FieldType.FILE ? translatedFileHelpText : '',
    required: false,
    order,
    options:
      type === FieldType.SELECT || type === FieldType.MULTISELECT
        ? ['', '']
        : [],
    value: '',
  };
  return field;
}

export function createPublishTemplate(
  template: CustomTemplateForm,
  translations: TranslatedStrings,
) {
  const { id, assignedTo, updatedBy, updatedAt, ...rest } =
    template.getRawValue();

  if (rest.name === '') rest.name = translations.templateTitle;
  if (rest.description === '')
    rest.description = translations.templateDescription;

  const sections = rest.sections.map((section) => {
    const { id, ...sectionRest } = section;

    const fields = sectionRest.fields.map((field) => {
      const { id, value, ...fieldRest } = field;
      if (!fieldRest.question) {
        fieldRest.question =
          fieldRest.type === FieldType.LOCATION
            ? translations.locationTitle
            : translations.questionTitle;
      }
      if (!fieldRest.placeholder)
        fieldRest.placeholder = translations.answerText;

      if (fieldRest.options.length > 0) {
        fieldRest.options = fieldRest.options.map((option, index) => {
          if (!option) return `Option ${index + 1}`;
          return option;
        });
      }

      return fieldRest as CreateFormField;
    });

    return {
      ...sectionRest,
      fields,
    } as CreateSection;
  });

  return {
    ...rest,
    sections,
  };
}

export function getSectionForm(
  templateForm: CustomTemplateForm,
  sectionIndex = 0,
): FormGroup<SectionForm> | null {
  if (
    sectionIndex < 0 ||
    sectionIndex >= templateForm.controls.sections.length
  ) {
    console.warn(`Section index ${sectionIndex} is out of bounds`);
    return null;
  }
  return templateForm.controls.sections.at(
    sectionIndex,
  ) as FormGroup<SectionForm>;
}

/**
 * Finds a field by id within a specific section
 */
export function findFieldIndexInSection(
  templateForm: CustomTemplateForm,
  fieldId: string,
  sectionIndex = 0,
): number {
  const section = getSectionForm(templateForm, sectionIndex);
  if (!section) return -1;

  return section.controls.fields.controls.findIndex(
    (control: { controls: { id: { value: string } } }) =>
      control.controls.id.value === fieldId,
  );
}

export function normalizeFieldOrders(section: FormGroup<SectionForm>): void {
  section.controls.fields.controls.forEach((field, index) => {
    field.controls.order.setValue(index + 1, { emitEvent: false });
  });
}
